import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/http';

export type PolicyImpact = 'NENHUM' | 'VALORES' | 'REGRA_DE_CALCULO';

export interface PolicyRule {
  topic: string;
  rule: string;
  quote: string;
  sourceUrl: string;
}

export interface PolicySource {
  id: string;
  name: string;
  url: string;
  active: boolean;
  lastCheckedAt: string | null;
  lastChangedAt: string | null;
  lastError: string | null;
}

export interface PolicyChangeSummary {
  id: string;
  provider: string;
  sourceId: string;
  sourceName: string;
  sourceUrl: string;
  status: 'PENDING' | 'REVIEWED';
  firstRead: boolean;
  changed: boolean | null;
  impact: PolicyImpact | null;
  connectorReviewNeeded: boolean | null;
  summary: string | null;
  analysisError: string | null;
  model: string | null;
  costUsd: string;
  createdAt: string;
}

export interface PolicyPlatform {
  provider: string;
  displayName: string;
  sources: PolicySource[];
  pendingChanges: number;
  currentVersion: { id: string; version: number; approvedAt: string; rules: PolicyRule[] } | null;
  versions: { id: string; version: number; approvedAt: string; note: string | null }[];
}

export interface PolicyReview {
  changed: boolean;
  impact: PolicyImpact;
  summary: string;
  changes: { topic: string; before: string; after: string; quote: string }[];
  connectorReviewNeeded: boolean;
  proposedRules: { topic: string; rule: string; quote: string }[];
  limitations: string[];
  confidence: 'ALTA' | 'MEDIA' | 'BAIXA';
}

export interface PolicyChangeDetail extends PolicyChangeSummary {
  analysis: PolicyReview | null;
  previousText: string | null;
  previousFetchedAt: string | null;
  currentText: string;
  currentFetchedAt: string;
  reviewNote: string | null;
  reviewedAt: string | null;
  approvedVersion: { id: string; version: number } | null;
  currentRules: PolicyRule[];
}

const keys = {
  all: ['platform', 'policies'] as const,
  pending: ['platform', 'policies', 'pending'] as const,
  change: (id: string) => ['platform', 'policies', 'change', id] as const,
};

/** `polling`: a conferência roda no worker; a tela acompanha só enquanto espera o resultado. */
export function usePolicies(polling = false) {
  return useQuery({
    queryKey: keys.all,
    queryFn: () =>
      apiRequest<{ platforms: PolicyPlatform[]; changes: PolicyChangeSummary[] }>(
        '/platform/policies',
      ),
    refetchInterval: polling ? 5_000 : false,
  });
}

/** Quantas mudanças esperam revisão: o aviso fixo do painel. Confere a cada 5 minutos. */
export function usePendingPolicies() {
  return useQuery({
    queryKey: keys.pending,
    queryFn: () =>
      apiRequest<{ pending: number; oldest: string | null }>('/platform/policies/pending'),
    refetchInterval: 5 * 60_000,
  });
}

export function usePolicyChange(id: string) {
  return useQuery({
    queryKey: keys.change(id),
    queryFn: () => apiRequest<PolicyChangeDetail>(`/platform/policies/changes/${id}`),
  });
}

function usePolicyMutation<TInput, TResult = void>(request: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.all }),
  });
}

export function useCheckPolicies() {
  return usePolicyMutation((sourceId: string | null) =>
    apiRequest<void>('/platform/policies/check', { method: 'POST', body: { sourceId } }),
  );
}

export function useToggleSource() {
  return usePolicyMutation(({ id, active }: { id: string; active: boolean }) =>
    apiRequest<void>(`/platform/policies/sources/${id}`, { method: 'PATCH', body: { active } }),
  );
}

export function useApprovePolicy(id: string) {
  return usePolicyMutation((input: { rules: PolicyRule[]; note: string | null }) =>
    apiRequest<{ version: number }>(`/platform/policies/changes/${id}/approve`, {
      method: 'POST',
      body: input,
    }),
  );
}

export function useReviewChange(id: string) {
  return usePolicyMutation((note: string) =>
    apiRequest<void>(`/platform/policies/changes/${id}/review`, { method: 'POST', body: { note } }),
  );
}

export function useReanalyzeChange(id: string) {
  return usePolicyMutation(() =>
    apiRequest<void>(`/platform/policies/changes/${id}/reanalyze`, { method: 'POST' }),
  );
}
